import { useMemo } from 'react';

import { useIsFocused } from '@sg/garnish';

import { checkIfCanBeReorderedByCustomer } from '@order/utils';

import { useIsLoggedIn } from '../../context/AuthMachine';
import {
  type MenuReorderRailOrdersQuery,
  useMenuReorderRailOrdersQuery,
} from './graphql/MenuReorderRailOrders.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useMenuReorderRailOrders = (params: Params) => {
  const { restaurantId, enabled, maxItems = MAX_ITEMS } = params;

  const isFocused = useIsFocused();
  const isLoggedIn = useIsLoggedIn();

  // ─── Remote Data ─────────────────────────────────────────────────────

  const [{ data, fetching }] = useMenuReorderRailOrdersQuery({
    variables: { input: { take: ORDERS_TO_FETCH, page: 1 }, restaurantId },
    pause: !enabled || !isLoggedIn || !isFocused,
    requestPolicy: 'cache-and-network',
  });

  // ─── Derived Data ────────────────────────────────────────────────────

  const orders = useMemo<Orders>(() => {
    const ordersData = data?.ordersV2;

    if (ordersData?.__typename !== 'OrdersResponseSuccess') return EMPTY_ORDERS;

    return ordersData.orders;
  }, [data]);

  const filteredLineItems = useMemo(() => {
    return orders
      .flatMap((order) => order.lineItems.map((lineItem) => ({ ...lineItem, order })))
      .filter(checkIfCanBeReorderedByCustomer)
      .filter((lineItem) => lineItem.canReorderAtRestaurant);
  }, [orders]);

  const mostRecentLineItems = useMemo(() => {
    return filteredLineItems.slice(0, maxItems);
  }, [filteredLineItems, maxItems]);

  const hasMoreLineItemsToSee = filteredLineItems.length > maxItems;

  const mostRecentOrder = orders[0];

  // ─────────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      isFetchingMostRecentOrders: fetching,
      orders,
      mostRecentOrder,
      mostRecentLineItems,
      hasMoreLineItemsToSee,
    }),
    [fetching, mostRecentLineItems, mostRecentOrder, orders, hasMoreLineItemsToSee],
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ORDERS_TO_FETCH = 10;
const MAX_ITEMS = 6;
const EMPTY_ORDERS: Orders = [];

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  restaurantId: number;
  enabled: boolean;
  maxItems?: number;
};

type Orders = NonNullable<
  Extract<MenuReorderRailOrdersQuery['ordersV2'], { __typename: 'OrdersResponseSuccess' }>['orders']
>;
